import { inject, Injectable } from '@angular/core';
import { EntityState } from '@ngrx/entity';

import {
  CanvasElement,
  CanvasElementId,
  FormOutputUpdateParams,
  WrapperRefs,
} from '@blaze-runtime/engine-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { deepClone, isEqual } from '@blaze-shared/shared-util';
import { StyleSheet } from '@blaze-shared/theming-domain';
import { FormActionType, setValueByPath } from '@blaze-shared/workflow-domain';

import { hasWrapperRefs } from '../../../canvas-elements/element-model/element-props-guards';
import {
  isElementType,
  isFieldGenerationSuspended,
  isVariantOfFormContainer,
} from '../../../canvas-elements/element-model/element-type-guards';
import { PregenerationData } from '../../../canvas-elements/element-model/pregeneration';
import { getWrapperRefs } from '../../../canvas-elements/element-model/wrapper-refs';
import {
  cleanRemovedFieldsMutably,
  COLUMN_CONTAINER_REFID,
  columnsToPregenerationData,
  COMMIT_BUTTON_REFID,
  FindFieldTargetPathData,
  prepareFieldElements,
  updateGenerationAndModelMismatchesMutably,
} from '../../../utils/elements/generation.utils';
import { ElementDefinitionsService } from '../../element-definitions.service';

import { FormColumnsLayoutService } from './form-columns-layout.service';
import { FormCommitButtonGenerationService } from './form-commit-button-generation.service';
import { FormContainerGenerationService } from './form-container-generation.service';
import { FormFieldGenerationService } from './form-field-generation.service';
import { FormGenerationData, FormLikeElement } from './form-generation-data';

/** What generating the form's fields produced, beyond the elements it wrote. */
interface GeneratedFields {
  /** The form outputs to re-point, for the fields whose element type changed. */
  formOutputUpdatesParams: FormOutputUpdateParams[];
  /** Whether the form ended up with any field at all — a form without one cannot commit. */
  hasFields: boolean;
}

/** What the caller needs to know about a form the editor has just (re)generated. */
interface FormGenerationResult {
  formId: CanvasElementId;
  commitButtonId: CanvasElementId | undefined;
  formActionType: FormActionType;
  formOutputUpdatesParams: FormOutputUpdateParams[];
  removedElementIds: CanvasElementId[];
}

/**
 * Generates a form from its data source: the form element, one input element per field it
 * shows, the columns they are laid out in, and the button that commits them.
 *
 * The generation writes into an entity state directly, so an effect can hand it a draft and
 * keep the whole regeneration in one store update.
 */
@Injectable({ providedIn: 'root' })
export class FormGenerationService {
  private readonly commitButton = inject(FormCommitButtonGenerationService);
  private readonly elementDefinitions = inject(ElementDefinitionsService);
  private readonly formColumns = inject(FormColumnsLayoutService);
  private readonly formContainer = inject(FormContainerGenerationService);
  private readonly formFields = inject(FormFieldGenerationService);
  private readonly schemaService = inject(SchemaService);

  /**
   * Non mutable version of regenerateFormMutably.
   * Fix for outdated state updates when AI orchestrator is creating elements.
   * Mutable version of this method could override entities updated by other effects.
   * Example: Form and Table generated by AI. This method was updating form
   * and removing data props from the table element.
   * Related task BL-9502
   */
  regenerateFormNonMutably(
    prevEntityState: EntityState<CanvasElement>,
    styleSheets: StyleSheet[],
    data: FormGenerationData,
  ): Omit<FormGenerationResult, 'removedElementIds'> & {
    elementIdsToRemove: CanvasElementId[];
    elementsToUpsert: CanvasElement[];
  } {
    const nextEntityState = deepClone(prevEntityState);
    const { formId, commitButtonId, formActionType, formOutputUpdatesParams, removedElementIds } =
      this.regenerateFormMutably(nextEntityState, styleSheets, data);
    const elementsToUpsert: CanvasElement[] = [];

    for (const elementId of nextEntityState.ids) {
      const prevElement = prevEntityState.entities[elementId];
      const nextElement = nextEntityState.entities[elementId];

      if (!nextElement || isEqual(prevElement, nextElement)) {
        continue;
      }

      elementsToUpsert.push(nextElement);
    }

    return {
      formId,
      commitButtonId,
      formActionType,
      formOutputUpdatesParams,
      elementIdsToRemove: removedElementIds,
      elementsToUpsert,
    };
  }

  /**
   * Creates or updates the form
   */
  regenerateFormMutably(
    entityState: EntityState<CanvasElement>,
    styleSheets: StyleSheet[],
    data: FormGenerationData,
  ): FormGenerationResult {
    const form = this.prepareFormMutably(entityState, styleSheets, data);
    const refs = getWrapperRefs(form);
    const model = form?.props?.content?.model;

    const action = form?.props?.content?.action;
    if (!action) {
      throw new Error('Form action missing');
    }

    let removedElementIds: CanvasElementId[] = [];
    let generatedFields: GeneratedFields = { formOutputUpdatesParams: [], hasFields: false };
    if (!isFieldGenerationSuspended(form)) {
      const pregenerationData = columnsToPregenerationData(
        data.inputDataSource?.columns || [],
        model,
        {
          isFormReadonly:
            action === 'view' ||
            data.pregenerationType === 'view' ||
            data.pregenerationType === 'view-dialog',
          isInsertForm: action === 'insert',
          overrideColumnVisibility: data.overrideColumnVisibility,
          fieldsLabelGen: form?.props?.featuresConfig?.fieldsLabelGen,
        },
      );
      updateGenerationAndModelMismatchesMutably(model, pregenerationData);

      // Remove tracked elements for fields that are no longer needed
      removedElementIds = cleanRemovedFieldsMutably(entityState, pregenerationData, refs);

      if (data.inputDataSource && data.pregenerationType !== 'delete') {
        generatedFields = this.addOrUpdateFieldsMutably(
          entityState,
          styleSheets,
          form,
          refs,
          pregenerationData,
          { ...data, inputDataSourceId: data.inputDataSource.id },
        );
      } else {
        // A form that generates no fields still owes the dialog its configured column count.
        this.applyColumnsCountMutably(
          entityState,
          form,
          data.columnsCount ?? 1,
          data.parentDialogId,
        );
      }
    }
    const { formOutputUpdatesParams, hasFields } = generatedFields;

    return {
      formId: form.id,
      commitButtonId: this.tryAddOrUpdateCommitButtonMutably(
        entityState,
        styleSheets,
        form,
        refs,
        action,
        hasFields,
        data.commitLabel,
      ),
      formActionType: action,
      formOutputUpdatesParams,
      removedElementIds,
    };
  }

  /**
   * Creates the form or brings the existing one up to date, applying a changed action.
   * @returns the form the rest of the generation writes into
   */
  private prepareFormMutably(
    entityState: EntityState<CanvasElement>,
    styleSheets: StyleSheet[],
    data: FormGenerationData,
  ): FormLikeElement {
    const existingForm = data.formId ? entityState.entities[data.formId] : undefined;
    // Form already present and the type is incorrect - throw error
    if (!!existingForm && !isVariantOfFormContainer(existingForm)) {
      throw new Error('Incorrect form type');
    }

    const { formId } = this.formContainer.addOrUpdateFormMutably(
      entityState,
      styleSheets,
      existingForm,
      data,
    );
    // Reassign the form so the new or up-to-date version is referenced.
    const form = entityState.entities[formId];
    if (
      form &&
      data.newAction &&
      (isElementType(form, 'formContainer') || isElementType(form, 'formDetails'))
    ) {
      form.type = data.newAction === 'view' ? 'formDetails' : 'formContainer';
      form.props.content.action = data.newAction;
    }
    if (!isVariantOfFormContainer(form)) {
      throw new Error('Form element error');
    }
    if (!hasWrapperRefs(form)) {
      setValueByPath(form.props, 'wrapper.refs', {});
      if (!hasWrapperRefs(form)) {
        throw new Error('Wrapper refs missing error');
      }
    }

    return form;
  }

  /** Adds or updates one input element per field the form shows. */
  private addOrUpdateFieldsMutably(
    entityState: EntityState<CanvasElement>,
    styleSheets: StyleSheet[],
    form: FormLikeElement,
    refs: WrapperRefs,
    pregenerationData: PregenerationData[],
    data: FormGenerationData & { inputDataSourceId: string },
  ): GeneratedFields {
    const fieldsCreationData = prepareFieldElements(
      form.id,
      pregenerationData,
      data.inputDataSourceId,
      'formContainer',
      data.sameDataSourceDifferentView,
      this.schemaService.getSchema.bind(this.schemaService),
      this.elementDefinitions.getDefinitionSafe.bind(this.elementDefinitions),
      this.formFields.initializeGeneratedFormFieldProps.bind(this.formFields),
      entityState,
      refs,
      styleSheets,
    );
    const isInitialGeneration = !data.formId;

    // Create a column container if field count is above the threshold
    // (the column container is created during the initial generation and if needed only)
    if (isInitialGeneration && this.formColumns.needsColumnContainer(fieldsCreationData.length)) {
      const { containerId, columnsCount } = this.formColumns.addOrUpdateColumnContainerMutably(
        undefined,
        entityState,
        form,
        fieldsCreationData.length,
      );
      refs[COLUMN_CONTAINER_REFID] = containerId;
      this.applyColumnsCountMutably(entityState, form, columnsCount, data.parentDialogId);
    }

    const findFieldTargetPathData: FindFieldTargetPathData<FormLikeElement> = {
      element: form,
      isInitialGeneration,
      totalFields: fieldsCreationData.length,
      fieldIndex: 0,
    };

    // Add or update each element
    const formOutputUpdatesParams: FormOutputUpdateParams[] = [];
    for (let i = 0; i < fieldsCreationData.length; i++) {
      findFieldTargetPathData.fieldIndex = i;
      const formUpdates = this.formFields.addOrUpdateFieldMutably(
        entityState,
        refs,
        fieldsCreationData[i],
        findFieldTargetPathData,
      );
      if (formUpdates) {
        formOutputUpdatesParams.push(formUpdates);
      }
    }

    // TODO: Reorder elements

    return { formOutputUpdatesParams, hasFields: fieldsCreationData.length > 0 };
  }

  /** Records the form's column count and widens the dialog it sits in to match. */
  private applyColumnsCountMutably(
    entityState: EntityState<CanvasElement>,
    form: FormLikeElement,
    columnsCount: number,
    parentDialogId: CanvasElementId | undefined,
  ): void {
    if (!form.props.content.options) {
      form.props.content.options = {};
    }
    form.props.content.options.columnsCount = columnsCount;
    this.formColumns.tryUpdateDialogWidthMutably(parentDialogId, columnsCount, entityState);
  }

  /**
   * Adds the commit button to the forms that commit on their own.
   * @returns the button's id, or `undefined` for a form whose group commits it
   */
  private tryAddOrUpdateCommitButtonMutably(
    entityState: EntityState<CanvasElement>,
    styleSheets: StyleSheet[],
    form: FormLikeElement,
    refs: WrapperRefs,
    action: FormActionType,
    hasFields: boolean,
    commitLabel: string | undefined,
  ): CanvasElementId | undefined {
    const origin = form?.props?.content?.origin;
    const isCommitButtonNeeded =
      hasFields &&
      (action === 'insert' || action === 'update' || action === 'prompt') &&
      origin !== 'form-group' &&
      origin !== 'wizard';
    if (!isCommitButtonNeeded) {
      return undefined;
    }

    const { commitButtonId } = this.commitButton.addOrUpdateCommitButtonMutably(
      entityState,
      styleSheets,
      form,
      refs[COMMIT_BUTTON_REFID],
      commitLabel,
    );
    refs[COMMIT_BUTTON_REFID] = commitButtonId;

    return commitButtonId;
  }
}
